import { clearChatQueues } from '@/lib/services/chat-queue-storage'

export const SHARE_GUEST_INTENT_KEY = 'fg-share-guest-intent'

const SHARE_GUEST_INTENT_TTL_MS = 30 * 60 * 1000

type ShareGuestIntentKind = 'study' | 'room'

interface ShareGuestIntent {
  kind: ShareGuestIntentKind
  shareToken: string
  expiresAt: number
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  return window.sessionStorage
}

function readShareGuestIntent(): ShareGuestIntent | null {
  const storage = getStorage()
  if (!storage) return null

  try {
    const raw = storage.getItem(SHARE_GUEST_INTENT_KEY)
    if (!raw) return null

    const value = JSON.parse(raw)
    if (
      typeof value?.kind !== 'string' ||
      !['study', 'room'].includes(value.kind) ||
      typeof value?.shareToken !== 'string' ||
      typeof value?.expiresAt !== 'number'
    ) {
      return null
    }

    return value
  } catch {
    return null
  }
}

function writeShareGuestIntent(kind: ShareGuestIntentKind, shareToken: string) {
  const storage = getStorage()
  if (!storage) return

  const value: ShareGuestIntent = {
    kind,
    shareToken,
    expiresAt: Date.now() + SHARE_GUEST_INTENT_TTL_MS,
  }

  storage.setItem(SHARE_GUEST_INTENT_KEY, JSON.stringify(value))
}

function getShareGuestTargetFromPath(
  pathname: string | null | undefined,
): { kind: ShareGuestIntentKind; shareToken: string } | null {
  if (!pathname) return null

  const pathOnly = pathname.split(/[?#]/)[0]
  const roomMatch = pathOnly.match(/^\/share\/room\/([^/]+)$/)
  if (roomMatch?.[1]) {
    return { kind: 'room', shareToken: decodeURIComponent(roomMatch[1]) }
  }

  const studyMatch = pathOnly.match(/^\/share\/([^/]+)$/)
  if (studyMatch?.[1]) {
    return { kind: 'study', shareToken: decodeURIComponent(studyMatch[1]) }
  }

  return null
}

export function getShareTokenFromPath(pathname: string | null | undefined): string | null {
  return getShareGuestTargetFromPath(pathname)?.shareToken ?? null
}

export function hasValidShareGuestIntent(pathname: string | null | undefined): boolean {
  const target = getShareGuestTargetFromPath(pathname)
  if (!target) return false

  const intent = readShareGuestIntent()
  if (!intent) return false

  if (
    intent.kind !== target.kind ||
    intent.shareToken !== target.shareToken ||
    intent.expiresAt <= Date.now()
  ) {
    clearShareGuestIntent()
    return false
  }

  return true
}

export function ensureShareGuestIntent(pathname: string | null | undefined): boolean {
  const target = getShareGuestTargetFromPath(pathname)
  if (!target) return false

  writeShareGuestIntent(target.kind, target.shareToken)
  return true
}

export function clearShareGuestIntent() {
  const storage = getStorage()
  if (!storage) return

  storage.removeItem(SHARE_GUEST_INTENT_KEY)
}

/**
 * Keys used by the public APIs in `client.ts` to persist the JWT issued by
 * `POST /public/reports/:shareToken/verify-email` (study) or
 * `POST /public/rooms/:shareToken/verify-email` (room). Separate keys keep the
 * two kinds independent so a guest can hold a valid OTP for both at once
 * without one clobbering the other.
 */
export const GUEST_STUDY_TOKEN_KEY = 'guestSessionToken'
export const GUEST_ROOM_TOKEN_KEY = 'guestRoomSessionToken'

type ShareKind = 'study' | 'room'

function tokenStorageKey(kind: ShareKind): string {
  return kind === 'study' ? GUEST_STUDY_TOKEN_KEY : GUEST_ROOM_TOKEN_KEY
}

interface GuestSessionPayload {
  sub?: unknown
  shareToken?: unknown
  type?: unknown
  kind?: unknown
  exp?: unknown
}

function decodeJwtPayload(token: string): GuestSessionPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    // atob handles URL-safe base64 after the replacements above. JWT payloads
    // are JSON, so any UTF-8 inside is encoded — decodeURIComponent reconstructs
    // multi-byte chars on the way out.
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )
    return JSON.parse(json) as GuestSessionPayload
  } catch {
    return null
  }
}

/**
 * Returns true when sessionStorage holds a non-expired `guest_session` JWT
 * scoped to the given share token AND share kind. Used by share pages to skip
 * the email gate on a refresh / re-open while a previous OTP is still valid.
 *
 * This is a client-side pre-check only — the backend re-validates the token
 * on every request. We intentionally do not verify the signature here.
 *
 * Backward-compat: legacy study tokens issued before the `kind` claim was
 * added are still accepted when `kind === 'study'`.
 */
export function hasValidGuestSessionToken(
  shareToken: string,
  kind: ShareKind = 'study',
): boolean {
  const storage = getStorage()
  if (!storage) return false

  const token = storage.getItem(tokenStorageKey(kind))
  if (!token) return false

  const payload = decodeJwtPayload(token)
  if (!payload) return false

  if (payload.type !== 'guest_session') return false
  if (payload.shareToken !== shareToken) return false

  const kindClaim = payload.kind
  const kindOk =
    kind === 'study'
      ? kindClaim === undefined || kindClaim === 'study'
      : kindClaim === 'room'
  if (!kindOk) return false

  if (typeof payload.exp !== 'number') return false
  if (payload.exp * 1000 <= Date.now()) return false

  return true
}

/**
 * The stored guest JWT for a share, or null when absent/expired/mismatched.
 * Callers that need to hand the token to a non-fetch transport (the chat
 * Socket.IO handshake) use this instead of reaching into sessionStorage.
 */
export function getGuestSessionToken(
  shareToken: string,
  kind: ShareKind = 'study',
): string | null {
  if (!hasValidGuestSessionToken(shareToken, kind)) return null
  return getStorage()?.getItem(tokenStorageKey(kind)) ?? null
}

/** Stable non-secret identity for per-viewer draft isolation, never a credential. */
export function getGuestSessionSubject(shareToken: string): string | null {
  try {
    const token = getGuestSessionToken(shareToken)
    const sub = token ? decodeJwtPayload(token)?.sub : null
    return typeof sub === 'string' || typeof sub === 'number' ? String(sub) : null
  } catch { return null }
}

export function clearGuestSessionToken(kind: ShareKind = 'study') {
  clearChatQueues()
  const storage = getStorage()
  if (!storage) return
  storage.removeItem(tokenStorageKey(kind))
}
